import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { computePricing } from "./pricing";
import { computeScore } from "./score";
import { SWOTKIT_LOGO } from "./swotkitLogo";

const TEAL = [13, 148, 136];
const ORANGE = [249, 115, 22];
const ROSE = [244, 63, 94];
const DARK = [15, 23, 42];
const SLATE = [71, 85, 105];
const MUTE = [148, 163, 184];
const LIGHT = [241, 245, 249];

const QUAD = {
  strengths: { label: "Strengths", tag: "INTERNAL", c: [16, 185, 129] },
  weaknesses: { label: "Weaknesses", tag: "INTERNAL", c: [245, 158, 11] },
  opportunities: { label: "Opportunities", tag: "EXTERNAL", c: [56, 189, 248] },
  threats: { label: "Threats", tag: "EXTERNAL", c: [244, 63, 94] },
};
const PRIO_C = { high: ROSE, medium: [217, 119, 6], low: [100, 116, 139] };

// Normalize a data-URL image into a clean PNG + its natural dimensions so
// jsPDF embeds it at full resolution with the correct format (no distortion).
function loadImageMeta(dataUrl) {
  return new Promise((resolve) => {
    if (!dataUrl) return resolve(null);
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || img.width || 1;
      const h = img.naturalHeight || img.height || 1;
      try {
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve({ dataUrl: canvas.toDataURL("image/png"), w, h, aspect: w / h });
      } catch (e) {
        resolve({ dataUrl, w, h, aspect: w / h });
      }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

export async function exportPlanPDF(plan, { premium = true } = {}) {
  const [logoImg, bannerImg] = await Promise.all([
    loadImageMeta(plan.logo),
    loadImageMeta(plan.brand_kit && plan.brand_kit.banner),
  ]);
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 42;
  const CW = W - 2 * M;
  let y = 0;

  const fill = (c) => doc.setFillColor(c[0], c[1], c[2]);
  const text = (c) => doc.setTextColor(c[0], c[1], c[2]);
  const PAGE_BODY = H - 54 - 50; // usable height on a fresh page
  const ensure = (h) => { if (y + h > H - 54) { footer(); doc.addPage(); y = 50; } };
  // Reserve space for a whole block so it isn't split mid-way (only when it can
  // actually fit on a fresh page; taller-than-page blocks fall back to flowing).
  const ensureBlock = (h) => ensure(Math.min(h, PAGE_BODY));

  const footer = () => {
    const n = doc.internal.getNumberOfPages();
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); text(MUTE);
    doc.text("Made with SwotKit", M, H - 26);
    doc.text(`Page ${n}`, W - M, H - 26, { align: "right" });
    doc.setFont("helvetica", "italic"); doc.setFontSize(6.5); text(MUTE);
    doc.text("Generated by SwotKit — a brainstorming aid, not professional advice.", W / 2, H - 15, { align: "center" });
  };

  // wrapped paragraph with line-height; returns new y
  const para = (str, x, maxW, size, lh, color, style = "normal") => {
    doc.setFont("helvetica", style); doc.setFontSize(size); text(color);
    const lines = doc.splitTextToSize(str || "", maxW);
    lines.forEach((ln) => { ensure(lh); doc.text(ln, x, y); y += lh; });
    return y;
  };

  const filterHigh = (arr) => premium ? (arr || []) : (arr || []).filter((i) => i.priority !== "high");

  // keepWith = height of the first content block that must stay with the heading
  const section = (title, keepWith = 60) => {
    y += 8; ensureBlock(34 + keepWith);
    fill(TEAL); doc.roundedRect(M, y - 2, 5, 18, 2, 2, "F");
    text(DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(15);
    doc.text(title, M + 14, y + 12);
    y += 30;
  };

  const drawBand = (topY) => {
    fill(DARK); doc.rect(0, topY, W, 86, "F");
    fill(TEAL); doc.rect(0, topY + 82, W, 3, "F");
    fill(ORANGE); doc.rect(0, topY + 85, W, 2, "F");
    try { doc.addImage(SWOTKIT_LOGO, "PNG", M, topY + 20, 22, 22); } catch (e) { /* noop */ }
    text([255, 255, 255]); doc.setFont("helvetica", "bold"); doc.setFontSize(16);
    doc.text("SwotKit", M + 30, topY + 32);
    text(MUTE); doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    doc.text("BUSINESS EVALUATION REPORT", M + 30, topY + 45);
    text([148, 163, 184]); doc.setFont("helvetica", "italic"); doc.setFontSize(9);
    doc.text("Validate your idea before you bet the business on it.", M, topY + 68);
    return 88;
  };

  const banner = bannerImg;
  if (banner) {
    // ---------- Brand banner as title ----------
    try { const bh = W / bannerImg.aspect; doc.addImage(bannerImg.dataUrl, "PNG", 0, 0, W, bh, undefined, "FAST"); y = bh + 22; } catch (e) { y = 30; }
    text(DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(22);
    doc.text(doc.splitTextToSize(plan.business_name || "Untitled", CW)[0] || "Untitled", M, y);
    y += 18;
    text(SLATE); doc.setFont("helvetica", "normal"); doc.setFontSize(10.5);
    const meta = [plan.industry, new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })].filter(Boolean).join("   ·   ");
    if (meta) { doc.text(meta, M, y); y += 6; }
    if (!premium) {
      y += 10; fill(ORANGE); doc.roundedRect(M, y - 10, 128, 18, 9, 9, "F");
      text([255, 255, 255]); doc.setFont("helvetica", "bold"); doc.setFontSize(8);
      doc.text("FREE PLAN — PREVIEW", M + 64, y + 2, { align: "center" });
    }
    y += 20;
  } else {
    // ---------- Dark header band ----------
    fill(DARK); doc.rect(0, 0, W, 132, "F");
    fill(TEAL); doc.rect(0, 128, W, 4, "F");
    fill(ORANGE); doc.rect(0, 132, W, 2, "F");
    if (logoImg) { try { fill([255, 255, 255]); doc.roundedRect(W - M - 66, 30, 66, 66, 10, 10, "F"); doc.addImage(logoImg.dataUrl, "PNG", W - M - 61, 35, 56, 56, undefined, "FAST"); } catch (e) { /* noop */ } }
    try { doc.addImage(SWOTKIT_LOGO, "PNG", M, 18, 20, 20); } catch (e) { /* noop */ }
    text([255, 255, 255]); doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.text("SwotKit", M + 27, 33);
    text(MUTE); doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.text("BUSINESS EVALUATION REPORT", M + 27, 44);
    text([255, 255, 255]); doc.setFont("helvetica", "bold"); doc.setFontSize(23);
    doc.text(doc.splitTextToSize(plan.business_name || "Untitled", CW - 90)[0] || "Untitled", M, 80);
    text([203, 213, 225]); doc.setFont("helvetica", "normal"); doc.setFontSize(10.5);
    if (plan.industry) doc.text(doc.splitTextToSize(plan.industry, CW - 90)[0], M, 98);
    text(MUTE); doc.setFontSize(8);
    doc.text(new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }), M, 112);
    text([148, 163, 184]); doc.setFont("helvetica", "italic"); doc.setFontSize(8.5);
    doc.text("Validate your idea before you bet the business on it.", M, 124);
    if (!premium) {
      fill(ORANGE); doc.roundedRect(W - M - 128, 100, 128, 18, 9, 9, "F");
      text([255, 255, 255]); doc.setFont("helvetica", "bold"); doc.setFontSize(8);
      doc.text("FREE PLAN — PREVIEW", W - M - 64, 112, { align: "center" });
    }
    y = 160;
  }

  // ---------- Overview ----------
  if (plan.description) { para(plan.description, M, CW, 10, 14, SLATE); y += 8; }

  // ---------- Readiness ----------
  const sc = computeScore(plan);
  ensure(60);
  text(DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("Readiness Score", M, y + 4);
  text(TEAL); doc.setFontSize(22);
  const scoreStr = `${sc.score}`;
  doc.text(scoreStr, M + 120, y + 6);
  const sw = doc.getTextWidth(scoreStr);
  text(MUTE); doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.text("/ 100", M + 120 + sw + 6, y + 6);
  text(ORANGE); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text(sc.grade, W - M, y + 5, { align: "right" });
  const barY = y + 16;
  fill(LIGHT); doc.roundedRect(M, barY, CW, 10, 5, 5, "F");
  fill(TEAL); doc.roundedRect(M, barY, Math.max(10, (CW * sc.score) / 100), 10, 5, 5, "F");
  y += 48;

  // ---------- SWOT ----------
  const colW = (CW - 18) / 2;
  const quads = ["strengths", "weaknesses", "opportunities", "threats"];
  const measure = (items) => {
    doc.setFontSize(9);
    let h = 44;
    (items.length ? items : [{ text: "—" }]).forEach((it) => {
      h += doc.splitTextToSize(it.text, colW - 30).length * 12 + 4;
    });
    return h + 6;
  };
  const swotRow0 = Math.max(measure(filterHigh(plan.swot?.[quads[0]])), measure(filterHigh(plan.swot?.[quads[1]])), 74);
  section("SWOT Analysis", swotRow0 + 14);
  for (let row = 0; row < 2; row++) {
    const a = filterHigh(plan.swot?.[quads[row * 2]]);
    const b = filterHigh(plan.swot?.[quads[row * 2 + 1]]);
    const cardH = Math.max(measure(a), measure(b), 74);
    ensureBlock(cardH + 14);
    const top = y;
    [0, 1].forEach((col) => {
      const key = quads[row * 2 + col];
      const meta = QUAD[key];
      const items = col === 0 ? a : b;
      const x = M + col * (colW + 18);
      fill([248, 250, 252]); doc.roundedRect(x, top, colW, cardH, 10, 10, "F");
      fill(meta.c); doc.roundedRect(x, top, colW, 24, 10, 10, "F"); doc.rect(x, top + 14, colW, 10, "F");
      text([255, 255, 255]); doc.setFont("helvetica", "bold"); doc.setFontSize(10.5);
      doc.text(meta.label, x + 12, top + 16);
      doc.setFontSize(7); doc.text(meta.tag, x + colW - 12, top + 16, { align: "right" });
      let iy = top + 40;
      doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      if (!items.length) { text(MUTE); doc.text(premium ? "—" : "High-impact items hidden on Free", x + 14, iy); }
      items.forEach((it) => {
        const pc = PRIO_C[it.priority] || PRIO_C.medium;
        const lines = doc.splitTextToSize(it.text, colW - 30);
        fill(pc); doc.circle(x + 14, iy - 3, 2, "F");
        text(SLATE);
        lines.forEach((ln, li) => { doc.text(ln, x + 22, iy + li * 12); });
        iy += lines.length * 12 + 4;
      });
    });
    y = top + cardH + 14;
  }

  // ---------- Competitor Tracker ----------
  section("Competitor Tracker", 72);
  if (premium) {
    autoTable(doc, {
      startY: y,
      head: [["Competitor", "Strengths", "Pricing", "Share", "Your Edge", "Threat", "Source"]],
      body: (plan.competitors || []).map((c) => [c.name, c.strengths, c.pricing, c.market_share, c.differentiation, c.threat_level, c.source || ""]),
      styles: { fontSize: 8, cellPadding: 7, valign: "top", textColor: SLATE, lineColor: [226, 232, 240], lineWidth: 0.5, overflow: "linebreak" },
      headStyles: { fillColor: TEAL, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8, cellPadding: 7 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 70 }, 2: { cellWidth: 56 }, 3: { cellWidth: 48 }, 5: { cellWidth: 44 } },
      margin: { left: M, right: M },
    });
    if (!plan.competitors?.length) { text(MUTE); doc.setFontSize(10); doc.text("No competitors added.", M, y + 16); y += 30; }
    else y = doc.lastAutoTable.finalY + 18;
  } else y = locked(doc, M, y, CW, fill, text) + 14;

  // ---------- Pricing ----------
  section("Pricing Calculator", premium ? 196 : 110);
  if (premium) {
    const p = computePricing(plan.pricing || {});
    const metrics = [["Recommended price", `$${p.price}`, ORANGE], ["Gross margin", `$${p.grossMargin} · ${p.marginPct}%`, [16, 185, 129]], ["Break-even / mo", `${p.breakEven} units`, [56, 189, 248]], ["Net profit / mo", `$${Number(p.netProfit).toLocaleString()}`, Number(p.netProfit) >= 0 ? [16, 185, 129] : ROSE]];
    const mW = (CW - 3 * 12) / 4;
    ensure(66);
    metrics.forEach((m, i) => {
      const x = M + i * (mW + 12);
      fill([248, 250, 252]); doc.roundedRect(x, y, mW, 60, 8, 8, "F");
      fill(m[2]); doc.roundedRect(x, y, mW, 4, 2, 2, "F");
      text(MUTE); doc.setFont("helvetica", "normal"); doc.setFontSize(7.5);
      doc.text(doc.splitTextToSize(m[0], mW - 14), x + 9, y + 18);
      text(m[2]); doc.setFont("helvetica", "bold"); doc.setFontSize(12);
      doc.text(doc.splitTextToSize(`${m[1]}`, mW - 14)[0], x + 9, y + 44);
    });
    y += 82;
    const bars = [["Price", Number(p.price), ORANGE], ["COGS", Number(plan.pricing?.cogs) || 0, [56, 189, 248]], ["Margin", Number(p.grossMargin), [16, 185, 129]]];
    const maxV = Math.max(...bars.map((b) => b[1]), 1);
    const chH = 72; ensure(chH + 34); const bottom = y + chH;
    bars.forEach((b, i) => {
      const bw = 64, gap = 40, bx = M + 16 + i * (bw + gap), bh = (b[1] / maxV) * chH;
      fill(b[2]); doc.roundedRect(bx, bottom - bh, bw, bh, 3, 3, "F");
      text(SLATE); doc.setFont("helvetica", "bold"); doc.setFontSize(8);
      doc.text(`$${Math.round(b[1])}`, bx + bw / 2, bottom - bh - 6, { align: "center" });
      text(MUTE); doc.setFont("helvetica", "normal"); doc.setFontSize(8);
      doc.text(b[0], bx + bw / 2, bottom + 14, { align: "center" });
    });
    y = bottom + 30;
  } else y = locked(doc, M, y, CW, fill, text) + 14;

  // ---------- 90-Day Plan ----------
  const phases = [{ k: "phase1", t: "Days 1–30 · Setup & Launch", c: TEAL }, { k: "phase2", t: "Days 31–60 · Traction & Sales", c: [56, 189, 248] }, { k: "phase3", t: "Days 61–90 · Scale & Optimize", c: [16, 185, 129] }];
  const phaseHeight = (tasks) => {
    doc.setFontSize(9);
    let h = 32; // header bar + gap
    if (!tasks.length) h += 16;
    tasks.forEach((t) => { h += Math.max(16, doc.splitTextToSize(t.text, CW - 90).length * 12 + 4); });
    return h + 12;
  };
  const plan0 = phaseHeight(filterHigh(plan.action_plan?.[phases[0].k]));
  section("90-Day Action Plan", plan0);
  phases.forEach((ph) => {
    const tasks = filterHigh(plan.action_plan?.[ph.k]);
    const phH = phaseHeight(tasks);
    // keep the whole phase together when it fits a page; else keep header + first task
    if (phH <= PAGE_BODY) ensureBlock(phH);
    else ensure(32 + 30);
    fill(ph.c); doc.roundedRect(M, y, CW, 22, 6, 6, "F");
    text([255, 255, 255]); doc.setFont("helvetica", "bold"); doc.setFontSize(10);
    doc.text(ph.t, M + 12, y + 15);
    y += 32;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    if (!tasks.length) { text(MUTE); ensure(14); doc.text(premium ? "—" : "High-impact items hidden on Free", M + 14, y); y += 16; }
    tasks.forEach((t) => {
      const lines = doc.splitTextToSize(t.text, CW - 90);
      const rowH = Math.max(16, lines.length * 12 + 4);
      ensure(rowH);
      doc.setDrawColor(ph.c[0], ph.c[1], ph.c[2]); doc.setLineWidth(0.8);
      doc.roundedRect(M + 8, y - 8, 10, 10, 2, 2, "D");
      if (t.done) { fill(ph.c); doc.roundedRect(M + 8, y - 8, 10, 10, 2, 2, "F"); text([255, 255, 255]); doc.setFontSize(8); doc.text("x", M + 10.5, y - 0.5); }
      text(SLATE); doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      lines.forEach((ln, li) => doc.text(ln, M + 28, y + li * 12));
      const pc = PRIO_C[t.priority] || PRIO_C.medium; text(pc); doc.setFontSize(7);
      doc.text(t.priority.toUpperCase(), W - M - 4, y, { align: "right" });
      y += rowH;
    });
    y += 12;
  });

  // ---------- Brand Kit (premium) ----------
  const bk = premium && plan.brand_kit && (plan.brand_kit.colors?.length || plan.brand_kit.banner);
  if (bk) {
    const bannerH = bannerImg ? CW / bannerImg.aspect : 0;
    section("Brand Kit", (bannerImg ? bannerH + 20 : 0) + (plan.brand_kit.tagline ? 24 : 0) + (plan.brand_kit.colors?.length ? 70 : 0));
    if (bannerImg) {
      try {
        const bw = CW, bh = CW / bannerImg.aspect; ensureBlock(bh + 10);
        doc.addImage(bannerImg.dataUrl, "PNG", M, y, bw, bh, undefined, "FAST");
        y += bh + 14;
      } catch (e) { /* noop */ }
    }
    if (plan.brand_kit.tagline) { para(`“${plan.brand_kit.tagline}”`, M, CW, 12, 16, DARK, "bolditalic"); y += 4; }
    if (plan.brand_kit.colors?.length) {
      ensureBlock(68);
      const sws = plan.brand_kit.colors.slice(0, 6);
      const swW = (CW - (sws.length - 1) * 10) / sws.length;
      sws.forEach((c, i) => {
        const x = M + i * (swW + 10);
        const rgb = hexRgb(c.hex);
        fill(rgb); doc.roundedRect(x, y, swW, 34, 6, 6, "F");
        text(SLATE); doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.text(c.name || "", x, y + 46);
        text(MUTE); doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.text((c.hex || "").toUpperCase(), x, y + 56);
      });
      y += 68;
    }
    if (plan.brand_kit.font) { text(MUTE); doc.setFont("helvetica", "normal"); doc.setFontSize(9); ensure(14); doc.text(`Suggested font: ${plan.brand_kit.font}`, M, y); y += 16; }
  }

  // ---------- AI Arena (premium) ----------
  const fr = premium && plan.arena && plan.arena.final_report;
  if (fr) {
    doc.setFontSize(11);
    const vLines = doc.splitTextToSize(fr.verdict || "", CW - 80);
    const boxH = Math.max(32, vLines.length * 13 + 16);
    section(`AI Arena — Winner: ${plan.arena.winner || "—"}`, boxH + 14);
    ensureBlock(boxH + 6);
    fill([240, 253, 250]); doc.roundedRect(M, y, CW, boxH, 8, 8, "F");
    text(DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    vLines.forEach((ln, i) => doc.text(ln, M + 12, y + 18 + i * 13));
    if (typeof fr.viability_score === "number") { text(TEAL); doc.setFontSize(11); doc.text(`${fr.viability_score}/100`, W - M - 12, y + 18, { align: "right" }); }
    y += boxH + 12;
    if (fr.summary) { para(fr.summary, M, CW, 9.5, 14, SLATE); y += 8; }
    const lists = [["KEY STRENGTHS", fr.key_strengths, [16, 185, 129]], ["KEY RISKS", fr.key_risks, ROSE], ["RECOMMENDED ACTIONS", fr.recommended_actions, [56, 189, 248]]];
    lists.forEach(([title, arr, c]) => {
      if (!(arr || []).length) return;
      doc.setFontSize(9);
      const firstH = doc.splitTextToSize(arr[0], CW - 16).length * 13 + 4;
      ensureBlock(18 + firstH); // keep title with its first bullet
      text(c); doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.text(title, M, y); y += 15;
      doc.setFont("helvetica", "normal");
      arr.forEach((xx) => {
        const lines = doc.splitTextToSize(xx, CW - 16);
        ensure(lines.length * 13 + 2);
        fill(c); doc.circle(M + 3, y - 3, 1.8, "F");
        text(SLATE); lines.forEach((ln, li) => doc.text(ln, M + 12, y + li * 13));
        y += lines.length * 13 + 4;
      });
      y += 8;
    });
  }

  // ---------- Closing SwotKit band ----------
  ensure(96);
  y += 14;
  drawBand(y);

  footer();
  doc.save(`${(plan.business_name || "business-plan").replace(/\s+/g, "-").toLowerCase()}-swotkit.pdf`);
}

function hexRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [100, 116, 139];
}

function locked(doc, x, y, w, fill, text) {
  const h = 92;
  fill(LIGHT); doc.roundedRect(x, y, w, h, 8, 8, "F");
  fill([226, 232, 240]);
  [0.9, 0.7, 0.8, 0.55].forEach((f, i) => doc.roundedRect(x + 16, y + 18 + i * 14, (w - 160) * f, 7, 3, 3, "F"));
  fill(ORANGE); doc.roundedRect(x + w - 156, y + h / 2 - 15, 140, 30, 15, 15, "F");
  text([255, 255, 255]); doc.setFont("helvetica", "bold"); doc.setFontSize(9);
  doc.text("PREMIUM — UPGRADE TO VIEW", x + w - 86, y + h / 2 + 3.5, { align: "center" });
  return y + h;
}
